import { css, StyleDeclarationValue, StyleSheet } from 'aphrodite';
import { Popover as PopoverPrimitive } from 'radix-ui';
import * as React from 'react';
import { shadowMd } from '../../styles/shadows';
import { THEME } from '../Theme/theme';

const slideInFromTopNFadeNZoom = {
	'0%': { transform: 'translateY(-8px) scale(0.95)', opacity: 0 },
	'100%': { transform: 'translateY(0) scale(1)', opacity: 1 },
};
const slideInFromBottomNFadeNZoom = {
	'0%': { transform: 'translateY(8px) scale(0.95)', opacity: 0 },
	'100%': { transform: 'translateY(0) scale(1)', opacity: 1 },
};
const slideInFromLeftNFadeNZoom = {
	'0%': { transform: 'translateX(-8px) scale(0.95)', opacity: 0 },
	'100%': { transform: 'translateX(0) scale(1)', opacity: 1 },
};
const slideInFromRightNFadeNZoom = {
	'0%': { transform: 'translateX(8px) scale(0.95)', opacity: 0 },
	'100%': { transform: 'translateX(0) scale(1)', opacity: 1 },
};
const fadeOutZoomOut = {
	'0%': { transform: 'scale(1)', opacity: 1 },
	'100%': { transform: 'scale(0.95)', opacity: 0 },
};

const s = StyleSheet.create({
	content: {
		boxSizing: 'border-box',
		// Matches the floating-overlay standard used by Select / Autocomplete /
		// Combobox / Dialog (all z-index 50). The Popover portals to the body, so
		// at 5 it rendered behind any Dialog (z-index 50) — e.g. the date-picker
		// calendar inside focused-view's CustomFollowUpModal.
		zIndex: 50,
		border: THEME.common.border,
		borderRadius: THEME.common.borderRadius,
		backgroundColor: THEME.colors.background.light,
		padding: `${THEME.spacing.sm}px ${THEME.spacing.md}px`,
		boxShadow: shadowMd,
		outline: 'none',
		animationDuration: '100ms',
		animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
		animationFillMode: 'forwards',
		':is([data-state="closed"])': { animationName: [fadeOutZoomOut] },
		':is([data-side="bottom"][data-state="open"])': { animationName: [slideInFromTopNFadeNZoom] },
		':is([data-side="top"][data-state="open"])': { animationName: [slideInFromBottomNFadeNZoom] },
		':is([data-side="left"][data-state="open"])': { animationName: [slideInFromRightNFadeNZoom] },
		':is([data-side="right"][data-state="open"])': { animationName: [slideInFromLeftNFadeNZoom] },
	},
	arrow: {
		fill: THEME.colors.background.light,
		stroke: '#f3f3f3',
		strokeWidth: 1,
		filter: 'drop-shadow(0 1px 1px rgba(162, 162, 162, .5))',
	},
});

const PopoverArrow = React.forwardRef<
	React.ElementRef<typeof PopoverPrimitive.Arrow>,
	React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Arrow> & { styles?: StyleDeclarationValue[] }
>(({ className = '', styles = [], ...props }, ref) => (
	<PopoverPrimitive.Arrow
		ref={ref}
		data-slot='popover-arrow'
		className={`${css(s.arrow, ...styles)} ${className}`.trim()}
		{...props}
	/>
));
PopoverArrow.displayName = PopoverPrimitive.Arrow.displayName;

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

const PopoverContent = React.forwardRef<
	React.ElementRef<typeof PopoverPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & { styles?: StyleDeclarationValue[] }
>(({ className = '', styles = [], align = 'center', sideOffset = 4, ...props }, ref) => (
	<PopoverPrimitive.Portal>
		<PopoverPrimitive.Content
			ref={ref}
			data-slot='popover-content'
			align={align}
			sideOffset={sideOffset}
			className={`${css(s.content, ...styles)} ${className}`.trim()}
			{...props}
		/>
	</PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

const PopoverClose = PopoverPrimitive.Close;

export { Popover, PopoverAnchor, PopoverArrow, PopoverContent, PopoverTrigger, PopoverClose };
